import { transform } from './transformer';
import { UploadFileDto } from './upload-file.dto';

export enum ComponentVersionStatus {
    UNPUBLISHED = 'Unpublished',
    PUBLISHED = 'Published',
}

export interface ComponentLifecycleRequest {
    install?: string;
    run?: string;
    startup?: string;
}

export interface ComponentManifestRequest {
    platformUuid: string;
    lifecycle?: ComponentLifecycleRequest;
    uploadedFileUuids?: string[];
    dockerImageUuids?: string[];
}

export interface CreateComponentVersionRequest {
    version: string;
    description?: string;
    configuration?: Record<string, unknown>;
    manifests: ComponentManifestRequest[];
}

export interface UpdateComponentVersionRequest {
    description?: string;
    configuration?: Record<string, unknown>;
    manifests: ComponentManifestRequest[];
}

export interface CreateComponentVersionQueryRequest {
    componentUuid: string;
}

export class ManifestPlatformDto {
    constructor(private readonly _object: Record<string, any>) {
    }

    get uuid(): string {
        return this._object['uuid'] || '';
    }

    get os(): string {
        return this._object['os'] || '';
    }

    get architecture(): string {
        return this._object['architecture'] || '';
    }

    get runtime(): string {
        return this._object['runtime'] || '';
    }

    get displayName(): string {
        return [this.os, this.architecture, this.runtime].filter(Boolean).join(' / ');
    }

    get raw(): Record<string, any> {
        return this._object;
    }
}

export class ComponentLifecycleDto {
    constructor(private readonly _object: Record<string, any>) {
    }

    get install(): string {
        return this._object['install'] || '';
    }

    get run(): string {
        return this._object['run'] || '';
    }

    get startup(): string {
        return this._object['startup'] || '';
    }

    get raw(): Record<string, any> {
        return this._object;
    }
}

export class ComponentVersionDockerImageDto {
    constructor(private readonly _object: Record<string, any>) {
    }

    get uuid(): string {
        return this._object['uuid'] || '';
    }

    get name(): string {
        return this._object['name'] || '';
    }

    get tag(): string {
        return this._object['tag'] || '';
    }

    get displayName(): string {
        return [this.name, this.tag].filter(Boolean).join(':');
    }

    get raw(): Record<string, any> {
        return this._object;
    }
}

export class ComponentManifestDto {
    constructor(private readonly _object: Record<string, any>) {
    }

    get name(): string {
        return this._object['name'] || '';
    }

    get platformUuid(): string {
        return this._object['platformUuid'] || this.platform?.uuid || '';
    }

    get platform(): ManifestPlatformDto | null {
        return this._object['platform'] ? transform(ManifestPlatformDto, this._object['platform']) : null;
    }

    get lifecycle(): ComponentLifecycleDto | null {
        return this._object['lifecycle'] ? transform(ComponentLifecycleDto, this._object['lifecycle']) : null;
    }

    get uploadedFileUuids(): string[] {
        if (Array.isArray(this._object['uploadedFileUuids'])) {
            return this._object['uploadedFileUuids'];
        }
        return this.uploadedFiles.map((file) => file.uuid);
    }

    get uploadedFiles(): UploadFileDto[] {
        return Array.isArray(this._object['uploadedFiles'])
            ? transform(UploadFileDto, this._object['uploadedFiles'])
            : [];
    }

    get dockerImageUuids(): string[] {
        return this.dockerImages.map((dockerImage) => dockerImage.uuid);
    }

    get dockerImages(): ComponentVersionDockerImageDto[] {
        return Array.isArray(this._object['dockerImages'])
            ? transform(ComponentVersionDockerImageDto, this._object['dockerImages'])
            : [];
    }

    get raw(): Record<string, any> {
        return this._object;
    }
}

export class ComponentVersionComponentDto {
    constructor(private readonly _object: Record<string, any>) {
    }

    get uuid(): string {
        return this._object['uuid'] || '';
    }

    get name(): string {
        return this._object['name'] || '';
    }

    get displayName(): string {
        return this.name;
    }

    get type(): string {
        return this._object['type'] || '';
    }

    get raw(): Record<string, any> {
        return this._object;
    }
}

export class ComponentVersionDto {
    constructor(private readonly _object: Record<string, any>) {
    }

    get uuid(): string {
        return this._object['uuid'] || '';
    }

    get version(): string {
        return this._object['version'] || '';
    }

    get id(): string {
        return this.uuid;
    }

    get description(): string | null {
        return this._object['description'] ?? null;
    }

    get componentUuid(): string {
        return this._object['componentUuid'] || this.component?.uuid || '';
    }

    get componentName(): string {
        return this._object['componentName'] || this.component?.displayName || '';
    }

    get componentType(): string {
        return this._object['componentType'] || this._object['type'] || this.component?.type || '';
    }

    get component(): ComponentVersionComponentDto | null {
        return this._object['component'] ? transform(ComponentVersionComponentDto, this._object['component']) : null;
    }

    get configuration(): Record<string, unknown> {
        return this._object['configuration'] || {};
    }

    get status(): ComponentVersionStatus {
        if (Object.values(ComponentVersionStatus).includes(this._object['status'])) {
            return this._object['status'];
        }
        return ComponentVersionStatus.UNPUBLISHED;
    }

    get manifests(): ComponentManifestDto[] {
        return Array.isArray(this._object['manifests'])
            ? transform(ComponentManifestDto, this._object['manifests'])
            : [];
    }

    get updatedAt(): Date {
        return this._object['updatedAt'] ? new Date(this._object['updatedAt']) : null;
    }

    get raw(): Record<string, any> {
        return this._object;
    }
}

export class ComponentVersionMessageResponseDto {
    constructor(private readonly _object: Record<string, any>) {
    }

    get message(): string {
        return this._object['message'] || '';
    }
}

export class RaptorComponentVersionViewModel extends ComponentVersionDto {
    constructor(
        public readonly componentVersion: ComponentVersionDto,
        public readonly accountUuid: string,
    ) {
        super(componentVersion.raw);
    }
}
